import {
  AppWindow,
  ArrowLeftRight,
  Blocks,
  BookOpen,
  BookOpenCheck,
  Building2,
  CalendarClock,
  CalendarRange,
  CheckCheck,
  Clock,
  CodeXml,
  Contact,
  CreditCard,
  FileSignature,
  FileText,
  Gauge,
  HandCoins,
  Hourglass,
  KeyRound,
  Landmark,
  LayoutDashboard,
  ListChecks,
  Package,
  Percent,
  Receipt,
  Repeat,
  Scale,
  ScrollText,
  Settings2,
  ShieldCheck,
  Sparkles,
  Store,
  Tag,
  TrendingUp,
  Users,
  UsersRound,
  Wallet,
  Webhook,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import type { Ability } from '@/auth/abilities';

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Hidden when the session lacks it. */
  ability?: Ability;
  keywords?: string[];
}

export interface NavSection {
  id: string;
  /**
   * In the sidebar, a labelled section is a group that opens and closes
   * under its own row; a section without a label is a run of pinned rows.
   * In the settings menu it is the heading above the links.
   */
  label?: string;
  /** The group's own icon (sidebar groups only). */
  icon?: LucideIcon;
  items: NavItem[];
}

/**
 * The sidebar. Pinned rows follow a deal from customer to payment; the
 * groups below hold the areas a role works in less often.
 */
export const NAVIGATION: NavSection[] = [
  {
    id: 'home',
    items: [
      {
        label: 'Overview',
        to: '/',
        icon: LayoutDashboard,
        ability: 'reports:read',
        keywords: ['dashboard', 'home', 'mrr'],
      },
      {
        label: 'Analytics',
        to: '/analytics',
        icon: TrendingUp,
        ability: 'reports:read',
        keywords: ['mrr', 'arr', 'churn', 'retention', 'nrr', 'cohorts', 'dso', 'metrics'],
      },
    ],
  },
  {
    id: 'billing',
    items: [
      {
        label: 'Customers',
        to: '/customers',
        icon: Users,
        ability: 'customers:read',
        keywords: ['accounts', 'clients'],
      },
      { label: 'Quotes', to: '/quotes', icon: FileText, ability: 'quotes:read', keywords: ['proposals'] },
      {
        label: 'Contracts',
        to: '/contracts',
        icon: FileSignature,
        ability: 'contracts:read',
        keywords: ['agreements', 'amendments'],
      },
      {
        label: 'Subscriptions',
        to: '/subscriptions',
        icon: Repeat,
        ability: 'subscriptions:read',
        keywords: ['plans', 'recurring'],
      },
      {
        label: 'Invoices',
        to: '/invoices',
        icon: Receipt,
        ability: 'invoices:read',
        keywords: ['bills', 'credit notes'],
      },
      {
        label: 'Payments',
        to: '/payments',
        icon: CreditCard,
        ability: 'payments:read',
        keywords: ['charges', 'refunds', 'cheque', 'eft'],
      },
    ],
  },
  {
    id: 'receivables',
    label: 'Receivables',
    icon: Hourglass,
    items: [
      {
        label: 'Aging',
        to: '/receivables/aging',
        icon: Clock,
        ability: 'receivables:read',
        keywords: ['ar', 'overdue'],
      },
      {
        label: 'Collections',
        to: '/receivables/collections',
        icon: ListChecks,
        ability: 'receivables:read',
        keywords: ['dunning', 'reminders'],
      },
      { label: 'Statements', to: '/receivables/statements', icon: ScrollText, ability: 'receivables:read' },
      {
        label: 'Payment plans',
        to: '/receivables/payment-plans',
        icon: CalendarClock,
        ability: 'receivables:read',
        keywords: ['installments'],
      },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: Landmark,
    items: [
      {
        label: 'Bank reconciliation',
        to: '/bank-reconciliation',
        icon: Scale,
        ability: 'payments:read',
        keywords: ['statements', 'camt.053', 'bai2', 'deposits', 'matching'],
      },
      {
        label: 'Revenue recognition',
        to: '/revenue',
        icon: CalendarClock,
        ability: 'reports:read',
        keywords: ['deferred revenue', 'asc 606', 'ifrs 15', 'waterfall', 'recognized'],
      },
      {
        label: 'Ledger',
        to: '/ledger',
        icon: BookOpen,
        ability: 'reports:read',
        keywords: ['journal', 'subledger', 'gl', 'accounts'],
      },
    ],
  },
  {
    id: 'commissions',
    label: 'Commissions',
    icon: HandCoins,
    items: [
      {
        label: 'Approvals',
        to: '/commissions',
        icon: CheckCheck,
        ability: 'commissions:read',
        keywords: ['commission', 'clawback', 'affiliate', 'disputes', 'attribution'],
      },
      {
        label: 'Participants',
        to: '/commissions/participants',
        icon: HandCoins,
        ability: 'commissions:read',
        keywords: ['affiliates', 'sales reps', 'agreements', 'plans', 'rates', 'quota'],
      },
      {
        label: 'Resellers',
        to: '/partners',
        icon: Store,
        ability: 'customers:read',
        keywords: ['partners', 'channel', 'reseller billing', 'margin'],
      },
      {
        label: 'Payouts',
        to: '/commissions/payouts',
        icon: Wallet,
        ability: 'commissions:read',
        keywords: ['payroll', 'accounts payable', 'export', 'csv'],
      },
      {
        label: 'Periods and statements',
        to: '/commissions/periods',
        icon: CalendarRange,
        ability: 'commissions:read',
        keywords: ['close', 'statements', 'exchange rates', 'fx'],
      },
    ],
  },
  {
    id: 'catalog',
    label: 'Catalog',
    icon: Package,
    items: [
      { label: 'Products', to: '/catalog/products', icon: Package, ability: 'catalog:read' },
      {
        label: 'Prices',
        to: '/catalog/prices',
        icon: Tag,
        ability: 'catalog:read',
        keywords: ['pricing', 'tiers'],
      },
      {
        label: 'Tax rates',
        to: '/catalog/tax-rates',
        icon: Percent,
        ability: 'catalog:read',
        keywords: ['gst', 'hst', 'pst', 'qst'],
      },
      {
        label: 'Features',
        to: '/catalog/features',
        icon: Sparkles,
        ability: 'catalog:read',
        keywords: ['entitlements'],
      },
      { label: 'Meters', to: '/catalog/meters', icon: Gauge, ability: 'catalog:read', keywords: ['usage'] },
    ],
  },
  {
    id: 'developers',
    label: 'Developers',
    icon: CodeXml,
    items: [
      {
        label: 'API keys',
        to: '/developers/api-keys',
        icon: KeyRound,
        ability: 'api_keys:manage',
        keywords: ['secret', 'tokens'],
      },
      {
        label: 'Webhooks',
        to: '/developers/webhooks',
        icon: Webhook,
        ability: 'webhooks:manage',
        keywords: ['endpoints'],
      },
      {
        label: 'Events',
        to: '/developers/events',
        icon: ArrowLeftRight,
        ability: 'webhooks:manage',
        keywords: ['logs'],
      },
      {
        label: 'Embedded components',
        to: '/developers/embed',
        icon: Blocks,
        ability: 'customers:read',
        keywords: ['embed', 'sdk', 'web components', 'widgets', 'origins', 'iframe'],
      },
      {
        label: 'Audit log',
        to: '/developers/audit-log',
        icon: ShieldCheck,
        ability: 'audit:read',
        keywords: ['history', 'security'],
      },
    ],
  },
];

/** The settings area's own menu (`SettingsLayout`); the sidebar shows one Settings row. */
export const SETTINGS_NAVIGATION: NavSection[] = [
  {
    id: 'organization',
    label: 'Organization',
    items: [
      {
        label: 'Team',
        to: '/settings/team',
        icon: UsersRound,
        ability: 'team:manage',
        keywords: ['members', 'roles', 'invite'],
      },
      {
        label: 'Legal entities',
        to: '/settings/legal-entities',
        icon: Building2,
        ability: 'settings:manage',
        keywords: ['issuer', 'company', 'subsidiary', 'invoice prefix', 'tax numbers'],
      },
    ],
  },
  {
    id: 'billing',
    label: 'Billing',
    items: [
      {
        label: 'Gateways',
        to: '/settings/gateways',
        icon: Landmark,
        ability: 'gateways:manage',
        keywords: ['moneris', 'rbc', 'pad', 'processors'],
      },
      {
        label: 'Customer portal',
        to: '/settings/portal',
        icon: AppWindow,
        ability: 'customers:read',
        keywords: ['self-service', 'portal', 'autopay'],
      },
      {
        label: 'Commissions',
        to: '/settings/commissions',
        icon: HandCoins,
        ability: 'commissions:read',
        keywords: ['commission', 'default rate', 'affiliate portal', 'periods'],
      },
    ],
  },
  {
    id: 'rules',
    label: 'Policies and rules',
    items: [
      {
        label: 'Collection policies',
        to: '/settings/collection-policies',
        icon: Settings2,
        ability: 'receivables:read',
        keywords: ['dunning'],
      },
      {
        label: 'Quote approvals',
        to: '/settings/approvals',
        icon: ShieldCheck,
        ability: 'quotes:read',
        keywords: ['approval', 'discount', 'rules', 'deal desk'],
      },
      {
        label: 'Automation rules',
        to: '/settings/automation',
        icon: Workflow,
        ability: 'settings:manage',
        keywords: ['rules', 'workflow', 'triggers', 'alerts', 'automation', 'notifications'],
      },
    ],
  },
  {
    id: 'integrations',
    label: 'Integrations',
    items: [
      {
        label: 'Accounting',
        to: '/settings/accounting',
        icon: BookOpenCheck,
        ability: 'settings:manage',
        keywords: ['quickbooks', 'xero', 'sync', 'integration', 'general ledger'],
      },
      {
        label: 'CRM',
        to: '/settings/crm',
        icon: Contact,
        ability: 'settings:manage',
        keywords: ['hubspot', 'salesforce', 'integration', 'sync', 'accounts', 'companies'],
      },
      {
        label: 'E-signatures',
        to: '/settings/e-signatures',
        icon: FileSignature,
        ability: 'settings:manage',
        keywords: ['zoho sign', 'docusign', 'signing', 'signature', 'certificate'],
      },
    ],
  },
];

/** Secondary destinations reachable from the palette only. */
export const EXTRA_DESTINATIONS: NavItem[] = [];

export interface Destination extends NavItem {
  /** Where the entry sits ("Receivables", "Settings"); pinned rows have none. */
  area?: string;
}

/** Every menu entry in menu order: the sidebar first, then settings. */
export const DESTINATIONS: Destination[] = [
  ...NAVIGATION.flatMap((section) =>
    section.items.map((item) => (section.label ? { ...item, area: section.label } : item)),
  ),
  ...SETTINGS_NAVIGATION.flatMap((section) => section.items.map((item) => ({ ...item, area: 'Settings' }))),
  ...EXTRA_DESTINATIONS,
];

const DESTINATION_PATHS = DESTINATIONS.map((item) => item.to);

/**
 * Whether `to` is the menu entry for `pathname`. An entry owns the pages
 * under it (`/customers` owns `/customers/:id`) unless another entry lives
 * there (`/commissions` does not own `/commissions/payouts`).
 */
export function isActivePath(pathname: string, to: string): boolean {
  if (pathname === to) return true;
  if (to === '/' || DESTINATION_PATHS.some((other) => other.startsWith(`${to}/`))) return false;
  return pathname.startsWith(`${to}/`);
}

/** The section whose entry is open, if any. */
export function activeSection(sections: readonly NavSection[], pathname: string): NavSection | undefined {
  return sections.find((section) => section.items.some((item) => isActivePath(pathname, item.to)));
}

/** The sections a session may see, without the entries it may not open. */
export function allowedSections(
  sections: readonly NavSection[],
  can: (ability: Ability) => boolean,
): NavSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !item.ability || can(item.ability)),
    }))
    .filter((section) => section.items.length > 0);
}

/** The first menu destination a session may open (the landing page for roles without reports). */
export function firstAllowedDestination(can: (ability: Ability) => boolean): string | null {
  return DESTINATIONS.find((item) => !item.ability || can(item.ability))?.to ?? null;
}

/** Where the sidebar's Settings row leads: the first settings page the session may open. */
export function firstSettingsDestination(can: (ability: Ability) => boolean): string | null {
  return allowedSections(SETTINGS_NAVIGATION, can)[0]?.items[0]?.to ?? null;
}

/** "g" then a key jumps to a section (shown in the shortcuts dialog). */
export const GOTO_SHORTCUTS: { key: string; to: string; label: string }[] = [
  { key: 'o', to: '/', label: 'Overview' },
  { key: 'c', to: '/customers', label: 'Customers' },
  { key: 'q', to: '/quotes', label: 'Quotes' },
  { key: 'k', to: '/contracts', label: 'Contracts' },
  { key: 's', to: '/subscriptions', label: 'Subscriptions' },
  { key: 'i', to: '/invoices', label: 'Invoices' },
  { key: 'p', to: '/payments', label: 'Payments' },
  { key: 'a', to: '/receivables/aging', label: 'Aging' },
  { key: 'r', to: '/catalog/prices', label: 'Prices' },
  { key: 'd', to: '/developers/api-keys', label: 'Developers' },
];
